"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Camera, Calendar, Cpu, Wrench, Layers, SlidersHorizontal as Sliders, FileCheck, Video } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
interface InspectionPoint {
  id: string;
  iconName: React.ComponentType<{
    className?: string;
  }>;
  title: string;
  description: string;
}
const inspectionPoints: InspectionPoint[] = [{
  id: "photos",
  iconName: Camera,
  title: "Exact Unit Photos",
  description: "Photos should represent the bull being offered rather than a generic stock image whenever possible."
}, {
  id: "year-use",
  iconName: Calendar,
  title: "Year and Previous Use",
  description: "If the manufacturing year or previous use is known, include it. If it is unknown, say so rather than guessing."
}, {
  id: "controller",
  iconName: Cpu,
  title: "Controller Condition",
  description: "The controller is an important part of the system. The listing should identify the controller supplied with the machine and note its visible condition where possible."
}, {
  id: "motion-base",
  iconName: Wrench,
  title: "Bull and Motion Base",
  description: "Look for obvious wear, corrosion, damage or previous repairs and document what is known."
}, {
  id: "inflatable",
  iconName: Layers,
  title: "Inflatable Safety Area",
  description: "Used inflatables can show cosmetic wear, repairs or replacement panels. Those details should be disclosed when known."
}, {
  id: "blower",
  iconName: Sliders,
  title: "Blower and Accessories",
  description: "Confirm which supporting components actually come with the unit."
}, {
  id: "service",
  iconName: FileCheck,
  title: "Service or Repair Information",
  description: "If service history is available, include it. If no records are available, do not imply that the machine has a documented maintenance history."
}, {
  id: "video",
  iconName: Video,
  title: "Operating Video Where Available",
  description: "An operating video can provide useful additional context for an individual unit when one is genuinely available."
}];
export default function BuyingUsed() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.06
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: "easeOut"
      }
    }
  };
  return <section id="buying-used" data-nav="light" className="relative w-full border-t border-border bg-background py-16 text-foreground md:py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        {/* Header Block */}
        <div className="mb-14 border-b border-border pb-10">
          <div className="max-w-3xl">
            <h2 className="font-serif text-3xl font-bold uppercase tracking-tight sm:text-4xl md:text-5xl">
              What We Look for in a Used Mechanical Bull Listing
            </h2>
            <p className="mt-4 font-sans text-base leading-relaxed text-muted-foreground sm:text-lg">
              A good used-equipment listing should help you understand the machine before you contact the seller. We therefore prioritize information about the individual unit wherever that information is available.
            </p>
          </div>
        </div>

        {/* 8-Point Verification Grid */}
        <div className="mb-16">
          <div className="mb-6">
            <h3 className="font-serif text-xl font-semibold uppercase tracking-wide text-foreground sm:text-2xl">
              What to look for
            </h3>
            <p className="font-sans text-sm text-muted-foreground">
              Information to review on each individual listing.
            </p>
          </div>

          <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
          once: true,
          amount: 0.15
        }} className="grid grid-cols-1 gap-px border border-border bg-border sm:grid-cols-2 lg:grid-cols-4">
            {inspectionPoints.map((point, index) => {
            const Icon = point.iconName;
            return <motion.div key={point.id} data-index={index} variants={itemVariants} className="group flex flex-col justify-between bg-card p-6 transition-[background-color] duration-150 hover:bg-muted">
                  <div>
                    <div className="mb-4 flex items-center justify-between">
                      <Icon className="h-5 w-5 text-primary" />
                    </div>

                    <h4 className="font-serif text-lg font-semibold uppercase tracking-tight text-foreground">
                      {point.title}
                    </h4>

                    <p className="mt-2.5 font-sans text-sm leading-relaxed text-muted-foreground">
                      {point.description}
                    </p>
                  </div>
                </motion.div>;
          })}
          </motion.div>
        </div>

        {/* Condition Information Narrative */}
        <div className="mb-16 border-t border-border pt-12">
          <div className="max-w-3xl">
            <h3 className="font-serif text-2xl font-bold uppercase tracking-tight text-foreground sm:text-3xl">
              Why Specific Condition Information Matters
            </h3>
            <p className="mt-4 font-sans text-base leading-relaxed text-muted-foreground sm:text-lg">
              Terms such as excellent condition or refurbished can mean different things to different sellers. A machine can look clean in photographs while still having unknown history in the controller, motion system or other components.
            </p>
            <p className="mt-3 font-sans text-base leading-relaxed text-muted-foreground sm:text-lg">
              For that reason, buyers should look for specific information about the individual machine rather than relying only on a broad condition label. Where a known issue, repair or cosmetic defect is available, it should be disclosed plainly so the buyer can evaluate it alongside the price.
            </p>

            <div className="mt-6">
              <Button asChild variant="outline" className="border-border px-5 py-2.5 font-serif text-sm uppercase tracking-wider text-foreground hover:bg-muted transition-colors">
                <Link to="/MechanicalBullBuyingGuidePage">Read Buying Guide</Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Condition vs. Age Subsection */}
        <div className="border-t border-border pt-12">
          <div className="max-w-3xl">
            <h3 className="font-serif text-2xl font-bold uppercase tracking-tight text-foreground sm:text-3xl">
              Used Mechanical Bull Condition Matters More Than Age Alone
            </h3>
            <p className="mt-4 font-sans text-base leading-relaxed text-muted-foreground sm:text-lg">
              Age can be useful, but it does not tell you everything about a used machine. A newer mechanical bull that received heavy commercial use may have more wear than an older unit that operated occasionally. Likewise, a machine with cosmetic marks may still be more attractive to a buyer than one with unknown controller or electrical issues.
            </p>
            <p className="mt-3 font-sans text-base leading-relaxed text-muted-foreground sm:text-lg">
              When comparing pre-owned equipment, consider the available usage history, controller condition, motion-base condition, visible wear, inflatable condition, blower condition, previous repairs, replacement components, equipment included and available documentation.
            </p>
            <p className="mt-3 font-sans text-base leading-relaxed text-muted-foreground sm:text-lg">
              Do not rely on one label such as good condition without looking at the information behind it. For expensive equipment, specific details are much more useful than broad descriptions.
            </p>
          </div>
        </div>
      </div>
    </section>;
}